import { useQuery, type UseQueryOptions, type UseQueryResult } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useAuthenticatedFetch } from '../useAuth';
import type { CommentNode, DiagramCommentFilters } from '../../domains/comments/models/Comment';
import { commentsKeys } from './commentsKeys';

type DiagramCommentsQueryKey = ReturnType<typeof commentsKeys.diagram>;

type DiagramCommentsQueryOptions = Omit<
  UseQueryOptions<CommentNode[], Error, CommentNode[], DiagramCommentsQueryKey>,
  'queryKey' | 'queryFn'
>;

interface DiagramCommentsResponse {
  comments: CommentNode[];
}

function buildDiagramCommentsUrl(diagramId: string, filters?: DiagramCommentFilters): string {
  const params = new URLSearchParams();
  if (filters?.state) {
    params.set('state', filters.state);
  }
  if (filters?.shapeId) {
    params.set('shapeId', filters.shapeId);
  }
  const query = params.toString();
  return query ? `/api/diagrams/${diagramId}/comments?${query}` : `/api/diagrams/${diagramId}/comments`;
}

function buildLinkDiagramCommentsUrl(diagramId: string, filters?: DiagramCommentFilters): string {
  return buildDiagramCommentsUrl(diagramId, filters).replace('/api/diagrams/', '/api/link/diagrams/');
}

export async function fetchDiagramComments(
  authenticatedFetch: (url: string, options?: RequestInit) => Promise<Response>,
  diagramId: string,
  filters?: DiagramCommentFilters,
): Promise<CommentNode[]> {
  let response = await authenticatedFetch(buildDiagramCommentsUrl(diagramId, filters));
  if (response.status === 401 || response.status === 403) {
    response = await fetch(buildLinkDiagramCommentsUrl(diagramId, filters), {
      credentials: 'include',
    });
  }
  if (!response.ok) {
    throw new Error('Failed to fetch comments');
  }
  const data: DiagramCommentsResponse = await response.json();
  return data.comments;
}

export function useDiagramCommentsQuery(
  diagramId: string | undefined,
  filters?: DiagramCommentFilters,
  options?: DiagramCommentsQueryOptions,
): UseQueryResult<CommentNode[], Error> {
  const authenticatedFetch = useAuthenticatedFetch();
  const stateFilter = filters?.state ?? null;
  const shapeFilter = filters?.shapeId ?? null;
  const enabled = (options?.enabled ?? true) && Boolean(diagramId);

  const normalizedFilters = useMemo<DiagramCommentFilters | undefined>(() => {
    const next: DiagramCommentFilters = {};
    if (stateFilter) {
      next.state = stateFilter;
    }
    if (shapeFilter) {
      next.shapeId = shapeFilter;
    }
    return Object.keys(next).length > 0 ? next : undefined;
  }, [shapeFilter, stateFilter]);

  const queryKey = useMemo(() => {
    if (!diagramId) {
      return commentsKeys.diagram('');
    }
    return commentsKeys.diagram(diagramId, normalizedFilters);
  }, [diagramId, normalizedFilters]);

  return useQuery<CommentNode[], Error, CommentNode[], DiagramCommentsQueryKey>({
    ...options,
    enabled,
    placeholderData:
      options?.placeholderData ??
      ((previous) => previous),
    queryKey,
    queryFn: async () => {
      if (!diagramId) {
        return [];
      }
      return fetchDiagramComments(authenticatedFetch, diagramId, normalizedFilters);
    },
  });
}
